import { MeetingCubit } from '@breakoutlearning/firebase-repository/cubits/MeetingCubit'
import { PageTitle } from 'components/PageTitle'
import { FullPageSpinner, Spinner } from 'components/Spinner'
import { inLocalDev } from 'config/environment'
import { useRepository } from 'hooks/auth'
import { useFeatureFlags } from 'hooks/featureFlags'
import { MeetingContextProvider } from 'hooks/meeting'
import { useObjectUrlCacheManager } from 'hooks/objectUrl'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useRouter } from 'hooks/router'
import { useTranslationTyped } from 'i18n/i18n'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { getAppCommitSha } from 'util/commitSha'
const MeetingPage = lazy(() => import('./MeetingPage'))
const AvCheck = lazy(() => import('./AvCheck'))

const MeetingPageTitle = observer(function MeetingPageTitle({
  meeting,
}: {
  meeting: MeetingCubit
}) {
  const { tt } = useTranslationTyped()

  if (!meeting) return null

  return (
    <PageTitle
      title={[
        tt.meeting.breakout_room(),
        meeting.slideDeck.data.slideDeckName,
      ].join(' | ')}
    />
  )
})

export function MeetingEntrypoint() {
  const rootStore = useRootStore()
  const router = useRouter()
  const params = router.params
  const roomId =
    params?.roomId && typeof params.roomId === 'string' ? params.roomId : null
  const currentUser = useBreakoutUser()
  const repository = useRepository()

  const [meeting, setMeeting] = useState<MeetingCubit | null>(null)

  const objectUrlCache = useObjectUrlCacheManager()
  const { t } = useTranslation()

  useEffect(() => {
    if (!roomId) return
    const meeting = new MeetingCubit(currentUser, repository, roomId)

    meeting.logEvent('meeting_start', {
      app_version: getAppCommitSha(),
    })

    // This is a hot reload cleanup - it cancels the timer when the component is
    // hot reload happens
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        // dispose all listeners and timers - if this component was rebuilt, it means
        // the old meeting is getting thrown out (but because it's a hot reload, the unmount method)
        // will not be called
        meeting.dispose()
      })
    }

    // Add an ability to skip the AV check in local development
    // The reason I want is to be able to quickly test the meeting without going through the AV check
    // Also, AV check switches the audio stack context in MacOS, which messes with the audio output
    // in my headphones, which is annoying when listening to music.
    //
    // Is it ridiculous? Yes.
    // Do I need it? Yes.
    // Am I kidding? No.
    if (
      inLocalDev() &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('dev_avCheckPassed') === 'true'
    ) {
      meeting.setAvCheckPassed(true)
    }

    meeting.initialize()
    setMeeting(meeting)

    return () => {
      meeting?.dispose()
      objectUrlCache.clear('meeting')
    }
  }, [currentUser, repository, roomId, objectUrlCache])

  useEffect(() => {
    if (!meeting) return
    return reaction(
      () => meeting.roomFailedToLoad,
      (failed) => {
        if (failed) {
          toast.error(t('meeting.room_failed_to_load'))
          rootStore.navigateTo('home')
        }
      },
      { fireImmediately: true }
    )
  }, [meeting, rootStore, t])

  const meetingContext = useMemo(() => {
    if (!meeting) return null
    return { meeting: meeting }
  }, [meeting])

  return (
    <>
      {meeting && <MeetingPageTitle meeting={meeting} />}
      <MeetingEntrypointWithAvCheck meetingContext={meetingContext} />
    </>
  )
}

const MeetingEntrypointWithAvCheck = observer(
  function MeetingEntrypointWithAvCheck({
    meetingContext,
  }: {
    meetingContext: { meeting: MeetingCubit } | null
  }) {
    const rootStore = useRootStore()
    const repository = useRepository()

    const featureFlags = useFeatureFlags()

    // Paywall protection
    useEffect(() => {
      // failsafe in case paywall starts causing issues
      if (featureFlags.enableMeetingPaywall !== true) return

      const meeting = meetingContext?.meeting
      if (!meeting) return

      return reaction(
        () => ({
          requiresPayment: meeting.requiresPayment,
          sectionId: meeting.sectionId,
          assignmentId: meeting.assignmentId,
          roomStateId: meeting.roomState.id,
        }),
        ({ requiresPayment, sectionId, assignmentId, roomStateId }) => {
          if (!sectionId || !assignmentId || !roomStateId) return

          if (requiresPayment) {
            rootStore.navigateToStudentAssignment(
              sectionId,
              assignmentId,
              roomStateId
            )
          }
        }
      )
    }, [featureFlags.enableMeetingPaywall, meetingContext?.meeting, rootStore])

    if (!meetingContext || repository.featureFlags.isLoading)
      return (
        <div className="absolute box-border h-full w-full">
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        </div>
      )

    const meeting = meetingContext.meeting

    // Wait for data to load
    const slidesLoaded = meeting.slideDeckSlides.isLoaded
    const roomStateLoaded = meeting.roomState.isLoaded
    const preMeetingAgreementUrlLoaded = meeting.preMeetingAgreementUrl.isLoaded

    if (!slidesLoaded || !roomStateLoaded || !preMeetingAgreementUrlLoaded) {
      return (
        <main className="absolute box-border h-full w-full" id="main">
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        </main>
      )
    }

    // demos have their own waiting room where you set up devices
    if (
      !meeting.isDemo &&
      !meeting.avCheckPassed &&
      !meeting.currentSlideIsEndOfSessionQuiz
    ) {
      return (
        <main id="main" className="absolute box-border h-full w-full">
          <Suspense fallback={<FullPageSpinner />}>
            <AvCheck meeting={meetingContext.meeting} />
          </Suspense>
        </main>
      )
    }

    const isReady = meetingContext

    return (
      <main className="absolute box-border h-full w-full" id="main">
        {!isReady && (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        )}
        {isReady && (
          <MeetingContextProvider value={meetingContext}>
            <Suspense fallback={<FullPageSpinner />}>
              <MeetingPage />
            </Suspense>
          </MeetingContextProvider>
        )}
      </main>
    )
  }
)
